/**
 * Questions Resource
 *
 * The account-wide "needs your input" list and a position's open questions at
 * `/v1/reasoning/questions`. A session's own questions, and answering them,
 * live on ChatSession.
 */

import type { HttpLayer } from '../http.js';
import { OpenQuestionsList } from '../questions/open-questions.js';
import type { ListOpenQuestionsRequest, ListOpenQuestionsResponse } from '../types/questions.js';

export class QuestionsResource {
  constructor(private http: HttpLayer) {}

  /**
   * List the open questions agents asked the user, newest first, across every
   * session of the account, with each question's session id, session title
   * and asker name. Pass `session_id` to list one session's open questions.
   */
  async listOpen(params: ListOpenQuestionsRequest = {}): Promise<ListOpenQuestionsResponse> {
    return this.http.get(
      '/v1/reasoning/questions',
      params.session_id ? { session_id: params.session_id } : undefined,
    );
  }

  /**
   * List the open questions waiting in the sessions whose primary is the
   * position's Agent, newest first: questions to the user and questions
   * addressed to an agent participant, each with its addressee. A position
   * with no Agent has none; an unknown position is a 404.
   */
  async listForPosition(positionId: string): Promise<ListOpenQuestionsResponse> {
    return this.http.get('/v1/reasoning/questions', { position_id: positionId });
  }

  /**
   * Open a live OpenQuestionsList of the account's questions to the user:
   * loaded now, then kept current by the event stream the host passes to its
   * `follow`. Frames for agent-addressed questions are ignored.
   */
  async connect(): Promise<OpenQuestionsList> {
    const list = new OpenQuestionsList(() => this.listOpen(), new Set(['user']));
    await list.refresh();
    return list;
  }

  /**
   * Open a live OpenQuestionsList of one position's open questions (as
   * `listForPosition`): loaded now, then kept current by the event stream the
   * host passes to its `follow`.
   */
  async connectForPosition(positionId: string): Promise<OpenQuestionsList> {
    const list = new OpenQuestionsList(() => this.listForPosition(positionId), new Set(['user', 'participant']));
    await list.refresh();
    return list;
  }
}
